import { Link } from 'react-router-dom';
import { AlertTriangle, Award, Wrench, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { useComplianceData } from '@/hooks/use-dashboard';

type AlertType = 'certification' | 'inspection' | 'maintenance';

interface CriticalAlert {
  id: string;
  assetId: number | null;
  assetName: string;
  type: AlertType;
  daysOverdue: number;
}

function isValidAssetId(assetId: number | null | undefined): assetId is number {
  return assetId !== null && assetId !== undefined && Number.isInteger(assetId) && assetId > 0;
}

function getAlertIcon(type: AlertType) {
  switch (type) {
    case 'certification':
      return <Award className="h-4 w-4 text-red-600" />;
    case 'inspection':
      return <AlertTriangle className="h-4 w-4 text-orange-600" />;
    case 'maintenance':
      return <Wrench className="h-4 w-4 text-yellow-600" />;
  }
}

function getAlertBadge(days: number) {
  if (days > 90) return <Badge variant="destructive">{days}d overdue</Badge>;
  if (days > 30) return <Badge className="bg-orange-600">{days}d overdue</Badge>;
  return <Badge className="bg-yellow-600">{days}d overdue</Badge>;
}

function getAlertTypeLabel(type: AlertType): string {
  switch (type) {
    case 'certification':
      return 'Expired Certification';
    case 'inspection':
      return 'Overdue Inspection';
    case 'maintenance':
      return 'Overdue Maintenance';
  }
}

export function CriticalAlerts() {
  const { data: complianceData, isLoading, isError } = useComplianceData();

  if (isLoading) {
    return (
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-gray-400" />
            Critical Alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !complianceData) {
    return (
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-gray-400" />
            Critical Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Unable to load compliance data</p>
        </CardContent>
      </Card>
    );
  }

  const alerts: CriticalAlert[] = [];

  (complianceData.expired_certifications ?? []).forEach((cert, index) => {
    alerts.push({
      id: `certification-${cert.asset_id ?? 'unresolved'}-${cert.due_date}-${index}`,
      assetId: isValidAssetId(cert.asset_id) ? cert.asset_id : null,
      assetName: cert.asset_serial || 'Unknown asset',
      type: 'certification',
      daysOverdue: cert.days_overdue,
    });
  });

  (complianceData.overdue_inspections ?? []).forEach((insp, index) => {
    alerts.push({
      id: `inspection-${insp.asset_id ?? 'unresolved'}-${insp.due_date}-${index}`,
      assetId: isValidAssetId(insp.asset_id) ? insp.asset_id : null,
      assetName: insp.asset_serial || 'Unknown asset',
      type: 'inspection',
      daysOverdue: insp.days_overdue,
    });
  });

  (complianceData.overdue_maintenance ?? []).forEach((maint, index) => {
    alerts.push({
      id: `maintenance-${maint.asset_id ?? 'unresolved'}-${maint.due_date}-${index}`,
      assetId: isValidAssetId(maint.asset_id) ? maint.asset_id : null,
      assetName: maint.asset_serial || 'Unknown asset',
      type: 'maintenance',
      daysOverdue: maint.days_overdue,
    });
  });

  const sortedAlerts = alerts
    .sort((a, b) => b.daysOverdue - a.daysOverdue)
    .slice(0, 5);

  if (sortedAlerts.length === 0) {
    return null;
  }

  return (
    <Card className="border-red-200 bg-red-50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Critical Alerts
            </CardTitle>
            <CardDescription>Top {sortedAlerts.length} most urgent items requiring immediate attention</CardDescription>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/gear/compliance">
              View All
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {sortedAlerts.map((alert) => {
            const content = (
              <>
                <div className="flex-shrink-0 p-2 bg-red-100 rounded-lg">
                  {getAlertIcon(alert.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{alert.assetName}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{getAlertTypeLabel(alert.type)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getAlertBadge(alert.daysOverdue)}
                  {alert.assetId !== null && <ChevronRight className="h-4 w-4 text-gray-400" />}
                </div>
              </>
            );
            const className = 'flex items-start gap-3 p-3 bg-white border border-red-200 rounded-lg';

            return alert.assetId !== null ? (
              <Link
                key={alert.id}
                to={`/assets/${alert.assetId}`}
                className={`${className} hover:bg-red-50 transition-colors`}
              >
                {content}
              </Link>
            ) : (
              <div key={alert.id} className={className}>
                {content}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
